import { TabOutlined } from "@mui/icons-material";
import { AgChartsEnterpriseModule } from "ag-charts-enterprise";
import { ModuleRegistry, RowSelectionModule } from "ag-grid-community";
import {
  ClipboardModule,
  ContextMenuModule,
  IntegratedChartsModule,
} from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { isEmpty, size } from "lodash";
import isNil from "lodash/isNil";
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useColumnTypes } from "../../../_src_shared/utils/agGrid/columnTypes/hooks/useColumnTypes";
import { useAggFuncs } from "../../../_src_shared/utils/agGrid/hooks/useAggFuncs";
import { useSidebar } from "../../../_src_shared/utils/agGrid/hooks/useSidebar";
import AgGridTableWrapper from "../../../components/agGrid/AgGridTableWrapper";
import UnsavedChangesAlert from "../../../components/common/UnsavedChangesAlert";
import { LOAN_PIPELINE_FILTER_LABEL_ALL } from "../../../constants/loanPipeline/LOAN_PIPELINE_FILTER_LABEL_ALL";
import { LOAN_STATUS_LABEL_ALL_ACTIVE } from "../../../constants/loanPipeline/LOAN_STATUS_LABEL_ALL_ACTIVE";
import { LOAN_STATUS_LABEL_ALL_CLOSED_LOANS } from "../../../constants/loanPipeline/LOAN_STATUS_LABEL_ALL_CLOSED_LOANS";
import { LOAN_STATUS_LABEL_ALL_INACTIVE_LOANS } from "../../../constants/loanPipeline/LOAN_STATUS_LABEL_ALL_INACTIVE_LOANS";
import { workspaceTabsAddMany } from "../../../redux/reducers/workspaceTabsReducer";
import { colDefFilterMultiSelect } from "../../../utils/agGrid/colDefs/colDefFilterMultiSelect";
import { useLoanPipelineScreenHook } from "../useLoanPipelineScreenHook";
import { useColumnDefsLoanPipelineTable } from "./hooks/useColumnDefsLoanPipelineTable";
import { useDefaultColumnDefsLoanPipelineTable } from "./hooks/useDefaultColumnDefsLoanPipelineTable";
import TooltipComponentLoanDataPreview from "../../../components/agGrid/tooltipComponent/TooltipComponentLoanDataPreview";
import { green } from "@mui/material/colors";

ModuleRegistry.registerModules([
  IntegratedChartsModule.with(AgChartsEnterpriseModule),
  ContextMenuModule,
  ClipboardModule,
  RowSelectionModule,
]);

const LoanPipelineTable = forwardRef(
  (
    {
      rowData,
      loading,
      quickFilterText,
      onStateUpdated,
      onLoanSelect,
      colDefs,
      height = "100%",
      pipelineFilter = null,
      loanStatusFilter = null,
      advancedFiltering = false,
      onFirstDataRendered,
      showTooltipHover,
      showTabber,
      setShowTabber,
    },
    ref,
  ) => {
    const navigate = useNavigate();
    const {
      loanStatusListActiveLoans,
      loanStatusListClosedLoans,
      loanStatusListInactiveLoans,
    } = useLoanPipelineScreenHook();
    const columnDefs = useColumnDefsLoanPipelineTable({ colDefs });
    const defaultColDef = useDefaultColumnDefsLoanPipelineTable({
      floatingFilter: advancedFiltering,
      filter: colDefFilterMultiSelect,
      cellClass: "ag-cell-vertical-center",
      minWidth: 200,
    });
    const columnTypes = useColumnTypes();
    const [selectedRows, setSelectedRows] = useState(null);
    const aggFuncs = useAggFuncs();
    const sideBar = useSidebar();

    const gridApiRef = useRef(null);

    const onGridReady = useCallback((params) => {
      gridApiRef.current = params.api;
    }, []);

    const isExternalFilterPresent = useCallback(() => {
      return !isNil(pipelineFilter) || !isNil(loanStatusFilter);
    }, [pipelineFilter, loanStatusFilter]);

    const doesExternalFilterPass = useCallback(
      (node) => {
        const data = node.data;
        if (!data) return true;

        // Handle pipeline filtering - skip if LOAN_PIPELINE_FILTER_LABEL_ALL
        if (
          !isNil(pipelineFilter) &&
          pipelineFilter !== "" &&
          pipelineFilter !== LOAN_PIPELINE_FILTER_LABEL_ALL
        ) {
          if (data.pipeline !== pipelineFilter) {
            return false;
          }
        }
        const status = data.loanStatus;

        if (loanStatusFilter === "All") {
          return true;
        }

        if (!isNil(loanStatusFilter) && loanStatusFilter !== "") {
          if (loanStatusFilter === LOAN_STATUS_LABEL_ALL_ACTIVE) {
            if (loanStatusListActiveLoans.includes(status)) {
              return true;
            }
          }

          if (loanStatusFilter === LOAN_STATUS_LABEL_ALL_INACTIVE_LOANS) {
            // Check if the status is in the inactive loans list
            if (loanStatusListInactiveLoans.includes(status)) {
              return true;
            }
          }

          // ---
          if (loanStatusFilter === LOAN_STATUS_LABEL_ALL_CLOSED_LOANS) {
            if (loanStatusListClosedLoans.includes(status)) {
              return true;
            }
          }
        }

        if (status !== loanStatusFilter) {
          return false;
        }

        return true;
      },
      [
        pipelineFilter,
        loanStatusFilter,
        loanStatusListActiveLoans,
        loanStatusListClosedLoans,
        loanStatusListInactiveLoans,
      ],
    );

    const onCellClicked = useCallback(
      (params) => {
        if (params?.colDef?.field === "loanNumber") {
          return;
        }

        const clickedElement = params.event?.target;

        if (
          (clickedElement instanceof Element &&
            clickedElement.closest("button")) ||
          (clickedElement instanceof Element && clickedElement.closest("a"))
        ) {
          return;
        }

        if (isNil(params?.data?.loanNumber)) {
          console.warn("No loanNumber found on this row");
          return;
        }

        if (params?.colDef?.field === "loanName") {
          navigate(`/loan/${params.data.loanNumber}`);
        }
      },
      [onLoanSelect, navigate],
    );

    const onTabberCancel = useCallback((props) => {
      setShowTabber(false);
      setSelectedRows(null);
    }, []);

    const rowSelection = useMemo(() => {
      if (showTabber) {
        return {
          mode: "multiRow",
        };
      }
    }, [showTabber]);

    const handleRowSelectionChange = useCallback((params) => {
      const selected = params.api.getSelectedRows();
      setSelectedRows(selected);
    }, []);

    const gridOptions = useMemo(() => {
      let baseOptions = {};
      if (showTooltipHover) {
        const tooltipOptions = {
          tooltipInteraction: true,
          tooltipShowDelay: 200,
          tooltipHideDelay: 100000,
        };
        baseOptions = { ...baseOptions, ...tooltipOptions };
      }
      return baseOptions;
    }, [showTooltipHover]);

    useEffect(() => {
      const api = gridApiRef.current;
      if (!api) return;

      api.onFilterChanged();
    }, [pipelineFilter, loanStatusFilter]);

    return (
      <AgGridTableWrapper
        height={height}
        size={2}
        noBorderRadius
        // sx={{
        //   ".ag-tooltip": {
        //     maxHeight: "90vh !important",
        //     overflow: "visible !important",
        //   },

        //   ".ag-tooltip-custom": {
        //     position: "fixed !important",
        //   },
        // }}
      >
        {!showTabber ? null : (
          <LoanTabberBar
            selectedRows={selectedRows}
            onCancel={onTabberCancel}
          />
        )}
        <AgGridReact
          ref={ref}
          onGridReady={onGridReady}
          rowData={rowData}
          columnDefs={colDefs || columnDefs}
          quickFilterText={quickFilterText}
          defaultColDef={defaultColDef}
          groupDisplayType={"groupRows"}
          onCellClicked={onCellClicked}
          columnTypes={columnTypes}
          sideBar={sideBar}
          onStateUpdated={onStateUpdated}
          enableCharts={true}
          rowSelection={rowSelection}
          onSelectionChanged={handleRowSelectionChange}
          cellSelection={true}
          includeHiddenColumnsInQuickFilter
          aggFuncs={aggFuncs}
          onFirstDataRendered={onFirstDataRendered}
          suppressAggFuncInHeader={true}
          loading={loading}
          // tooltipMouseTrack
          // tooltipShowDelay={200}

          // tooltipInteraction
          // tooltipShowDelay={500}
          // tooltipHideDelay={100000}
          gridOptions={gridOptions}
          isExternalFilterPresent={isExternalFilterPresent}
          doesExternalFilterPass={doesExternalFilterPass}
        />
      </AgGridTableWrapper>
    );
  },
);

const LoanTabberBar = ({ selectedRows, onCancel }) => {
  const dispatch = useDispatch();

  const onAddToTabs = (d) => {
    const mappedData = selectedRows.map((row) => ({
      loanNumber: row.loanNumber,
      loanName: row.loanName,
    }));

    // console.log("mappedData", mappedData);
    // return;
    dispatch(workspaceTabsAddMany(mappedData));
    onCancel();
  };
  return (
    <UnsavedChangesAlert
      onSubmit={onAddToTabs}
      icon={<TabOutlined className="thin-9" />}
      onCancel={onCancel}
      text={
        isEmpty(selectedRows)
          ? "Select loans to add to tabs"
          : `Selected loans (${size(selectedRows)})`
      }
      disabled={isEmpty(selectedRows)}
      saveText={"Add To Tabs"}
    />
  );
};
export default LoanPipelineTable;
